import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { Box, Grid } from "@mui/system";
import { DateCalendar } from "@mui/x-date-pickers";
import React from "react";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import { CalendarSlot } from "src/app/models/screens/requestManagement.model";
import MyCalendarViewModel from "src/app/screens/myCalendar/myCalendar.viewmodel";
import styles from "src/app/styles/screens/myCalendar.screen.style";

const MyCalendarScreen: React.FC = () => {
  const {
    ownCalendarDate,
    onOwnCalendarDateChanged,
    isLoadingOwnAcceptedRequests,
    acceptedRequestsForOwnSelectedDate,
    minutesToLabel,
    isDisplayingSnackbar,
    isSnackbarError,
    setIsDisplayingSnackbar,
    snackbarMessage,
    role,
    selectorUsers,
    isLoadingChildren,
    selectedCalendarOwnerId,
    onSelectedCalendarOwnerChanged,
  } = MyCalendarViewModel();
  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="My Calendar" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Box sx={styles.selectedTab}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              My Calendar
            </Typography>
          </Grid>
          {(role === "Parent" || role === "Admin") && (
            <Grid container justifyContent="flex-end" mt={2}>
              <Grid size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}>
                <Paper elevation={0} sx={styles.selectedTabContent}>
                  <FormControl fullWidth>
                    <InputLabel id="calendar-owner-select-label">
                      Calendar Owner
                    </InputLabel>
                    <Select
                      labelId="calendar-owner-select-label"
                      value={selectedCalendarOwnerId}
                      label="Calendar Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedCalendarOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {selectorUsers.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid container spacing={3} mt={2}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper elevation={0} sx={styles.calendarContainer}>
                <DateCalendar
                  value={ownCalendarDate}
                  onChange={onOwnCalendarDateChanged}
                />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper elevation={0} sx={styles.selectedTabContent}>
                <Typography sx={styles.selectedTabText}>
                  Selected Date Schedule
                </Typography>
                {isLoadingOwnAcceptedRequests ? (
                  <Box sx={styles.searchingLoader}>
                    <CircularProgress size={28} />
                  </Box>
                ) : acceptedRequestsForOwnSelectedDate.length === 0 ? (
                  <Typography sx={styles.noUsersText}>
                    Nothing scheduled for selected date
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {acceptedRequestsForOwnSelectedDate.map(
                      (request: CalendarSlot) => (
                        <Grid key={request.id} size={{ xs: 12 }}>
                          <Paper elevation={0} sx={styles.selectedTabContent}>
                            {request.requesterFullName && (
                              <Typography sx={styles.requesterNameText}>
                                Appointment with {request.requesterFullName}
                              </Typography>
                            )}
                            <Typography sx={styles.requesterDate}>
                              {request.emergency
                                ? `Time: ${minutesToLabel(request.from)}`
                                : `Time: ${minutesToLabel(request.from)} - ${minutesToLabel(request.to)}`}
                            </Typography>
                            <Typography>
                              Description: {request.description}
                            </Typography>
                          </Paper>
                        </Grid>
                      ),
                    )}
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DrawerAwareContainerComponentView>
      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default MyCalendarScreen;
